import { useState, useCallback, useEffect } from 'react';
import {
  formatDate,
  IconTrash,
  IconEdit,
  Textarea,
  ButtonSize,
  ButtonPseudo,
  ButtonSecondary,
  Popup,
} from '@ui/src';
import {
  useDeleteNewApplicationCommentMutation,
  useUpdateNewApplicationCommentMutation,
} from '../../hoc/new-application/store/services';
import { CommentItemTypes } from './types';

import cn from './new-application.module.css';

const CommentItem = ({ comment, applicationId }: CommentItemTypes) => {
  const [commentValue, setCommentValue] = useState(comment?.text);
  const [edit, setEdit] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);

  const [
    updateComment,
    { isLoading: updateIsLoading, isSuccess: updateIsSuccess },
  ] = useUpdateNewApplicationCommentMutation();
  const [
    deleteComment,
    { isLoading: deleteInProgress, isSuccess: deleteIsSuccess },
  ] = useDeleteNewApplicationCommentMutation();

  const updateCommentAction = useCallback(() => {
    updateComment({
      id: applicationId as string,
      commentId: comment.id,
      body: {
        text: commentValue,
      },
    });
  }, [commentValue]);

  useEffect(() => {
    if (deleteIsSuccess) {
      setPopupVisible(false);
    }
  }, [deleteIsSuccess]);

  useEffect(() => {
    if (updateIsSuccess) {
      setEdit(false);
    }
  }, [updateIsSuccess]);

  let content;

  if (!edit) {
    content = <div className={cn.commentText}>{commentValue}</div>;
  } else {
    content = (
      <>
        <Textarea
          name="comment"
          placeholder="Enter comment here..."
          onChange={(e) => setCommentValue(e.target.value)}
          value={commentValue}
          className="borderless"
        />
        <div className={cn.processingResultCommentControls}>
          <ButtonSecondary
            text={updateIsLoading ? 'Loading...' : 'Update Comment'}
            onClick={updateCommentAction}
            className={`button-white ${cn.sendCommentButton}`}
            size={ButtonSize.SMALL}
          />
          <ButtonPseudo
            text="Cancel"
            onClick={() => setEdit(false)}
            size={ButtonSize.SMALL}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <Popup
        title="Delete comment?"
        onCancelClick={() => setPopupVisible(false)}
        onOkClick={() =>
          deleteComment({
            id: applicationId as string,
            commentId: comment.id,
          })
        }
        cancelButtonText="Cancel"
        okButtonText={deleteInProgress ? 'Loading...' : 'Yes, delete'}
        isVisible={popupVisible}
      />
      <div className={cn.comment}>
        <div className={cn.commentTop}>
          <div className={cn.commentTopLeft}>
            <div className={cn.commentDate}>
              {formatDate(comment?.createdAt, true)}
            </div>
            <div
              className={cn.commentAuthor}
            >{`${comment?.user?.profile?.firstName} ${comment?.user?.profile?.lastName}`}</div>
          </div>
          {!edit && (
            <div className={cn.commentTopRight}>
              <button
                type="button"
                className={cn.commentControlButton}
                onClick={() => setEdit(true)}
                aria-label="Edit button"
              >
                <IconEdit />
              </button>
              <button
                type="button"
                className={`${cn.commentControlButton} ${cn.commentDeleteButton}`}
                onClick={() => setPopupVisible(true)}
                aria-label="Delete button"
              >
                <IconTrash />
              </button>
            </div>
          )}
        </div>
        {content}
      </div>
    </>
  );
};

export default CommentItem;
